import * as THREE from 'three'
import { scalePoint, scaleLinear } from 'd3-scale'
import { pointFrag, pointVert } from '@app/shaders/point'
import { alphaFrag, alphaVert } from '@app/shaders/alpha'
import { addQuad } from '@app/views/geomUtils'
import { formatLap } from '@app/views/utils'

const circle = new THREE.TextureLoader().load(
  '/static/f1times/data/circle-sdf.png'
)

const triangle = new THREE.TextureLoader().load(
  '/static/f1times/data/triangle-sdf.png'
)

const material = new THREE.LineBasicMaterial({
  color: 0xdddddd,
})

const barOutlineMaterial = new THREE.MeshBasicMaterial({
  color: 0x4669e4,
})

const barGreyOutlineMaterial = new THREE.MeshBasicMaterial({
  color: 0xdddddd,
})

const backgroundMaterial = new THREE.MeshBasicMaterial({
  color: 0xf6f6f6,
})

const barsMaterial = new THREE.ShaderMaterial({
  side: THREE.DoubleSide,
  vertexShader: alphaVert(),
  fragmentShader: alphaFrag(),
  transparent: true,
})

const pointsMaterial = new THREE.ShaderMaterial({
  vertexShader: pointVert(),
  fragmentShader: pointFrag(),
  transparent: true,
  uniforms: {
    scale: { value: 1 },
    circle: { value: circle },
    triangle: { value: triangle },
  },
  depthTest: false,
})

interface ScatterplotDatum {
  x: string
  y: number
  color: number[]
  outline: number
  aboveDomain: boolean
}

interface Margin {
  top: number
  right: number
  bottom: number
  left: number
}

interface Label {
  point: THREE.Vector3
  text: string
  rotate: boolean
  className: string
}

interface Detail {
  name: string
  value: string
}

export class Scatterplot {
  rect: ClientRect
  xDomain: string[]
  xTicks: string[]
  yDomain: number[]
  yTicks: number[]
  xScale: any
  yScale: any
  data: ScatterplotDatum[]
  margin: Margin
  scene: THREE.Scene
  labels: Label[]
  title: string
  message: string
  points: THREE.Points
  bars: THREE.Mesh
  pixelRatio: number
  outlinedXValue?: number
  activeXValue?: number

  constructor({
    rect,
    xDomain,
    xTicks,
    yDomain,
    yTicks,
    data,
    margin,
    title,
    message,
    pixelRatio,
    outlinedXValue,
    activeXValue,
  }: {
    rect: ClientRect
    xDomain: string[]
    xTicks: string[]
    yDomain: number[]
    yTicks?: number[]
    data: ScatterplotDatum[]
    margin: Margin
    title: string
    message?: string
    pixelRatio: number
    outlinedXValue?: number
    activeXValue?: number
  }) {
    this.rect = rect
    this.xDomain = xDomain
    this.xTicks = xTicks
    this.yDomain = yDomain
    this.data = data
    this.margin = margin
    this.title = title
    this.message = message
    this.pixelRatio = pixelRatio
    this.outlinedXValue = outlinedXValue
    this.activeXValue = activeXValue

    this.xScale = scalePoint()
      .domain(this.xDomain)
      .range([this.margin.left, this.rect.width - this.margin.right])

    this.yScale = scaleLinear()
      .domain(this.yDomain)
      .range([this.rect.height - this.margin.bottom, this.margin.top])

    this.scene = new THREE.Scene()
    this.scene.position.set(
      this.rect.left + this.rect.width / 2,
      this.rect.top + this.rect.height / 2,
      0
    )

    const container = new THREE.Group()
    this.scene.add(container)
    container.position.set(-this.rect.width / 2, -this.rect.height / 2, 0)

    const fromX = this.xScale.range()[0]
    const toX = this.xScale.range()[1]
    const fromY = this.yScale.range()[0]
    const toY = this.yScale.range()[1]
    const z = -15
    const backgroundGeometry = new THREE.BufferGeometry()
    backgroundGeometry.addAttribute(
      'position',
      new THREE.Float32BufferAttribute(
        addQuad([], [fromX, fromY], [toX, toY], z),
        3
      )
    )

    const background = new THREE.Mesh(backgroundGeometry, backgroundMaterial)
    container.add(background)

    const width = this.xScale.range()[1] - this.xScale.range()[0]
    const xSpan = Math.min(2 + width / this.xDomain.length, 15)
    const barVertices: number[] = []
    const barColors: number[] = []
    const barZ = 5
    const offset = 0.5 * this.pixelRatio
    for (let i = 0; i < this.xDomain.length; i++) {
      const centerXBar = this.xScale(this.xDomain[i])
      let z = barZ
      if (i === this.activeXValue) {
        z = barZ + offset * 3
      }

      if (i === this.outlinedXValue) {
        z = barZ + offset * 5
      }

      addQuad(
        barVertices,
        [centerXBar + xSpan / 2, fromY],
        [centerXBar - xSpan / 2, toY],
        z
      )

      let barColor = [1, 1, 1, 0]
      if (i === this.activeXValue) {
        barColor = [1, 1, 1, 1]
      } else if (i === this.outlinedXValue) {
        barColor = [0.975, 0.975, 0.975, 1]
      }

      barColors.push(
        ...[].concat(barColor, barColor, barColor, barColor, barColor, barColor)
      )
    }

    const barsGeometry = new THREE.BufferGeometry()

    barsGeometry.addAttribute(
      'position',
      new THREE.Float32BufferAttribute(barVertices, 3)
    )

    barsGeometry.addAttribute(
      'color',
      new THREE.Float32BufferAttribute(barColors, 4)
    )

    barsGeometry.computeBoundingSphere()

    this.bars = new THREE.Mesh(barsGeometry, barsMaterial)
    container.add(this.bars)

    if (this.activeXValue !== null) {
      const centerXBar = this.xScale(this.xDomain[this.activeXValue])
      const barOutlineVertices: number[] = []
      addQuad(
        barOutlineVertices,
        [centerXBar - xSpan / 2 - offset, fromY + offset],
        [centerXBar + xSpan / 2 + offset, toY - offset],
        barZ + offset * 2
      )

      const barOutlineGeometry = new THREE.BufferGeometry()

      barOutlineGeometry.addAttribute(
        'position',
        new THREE.Float32BufferAttribute(barOutlineVertices, 3)
      )

      barsGeometry.computeBoundingSphere()
      const barOutline = new THREE.Mesh(
        barOutlineGeometry,
        barGreyOutlineMaterial
      )
      container.add(barOutline)
    }

    if (this.outlinedXValue !== null) {
      const centerXBar = this.xScale(this.xDomain[this.outlinedXValue])
      const barOutlineVertices: number[] = []
      addQuad(
        barOutlineVertices,
        [centerXBar - xSpan / 2 - offset, fromY + offset],
        [centerXBar + xSpan / 2 + offset, toY - offset],
        barZ + offset * 4
      )

      const barOutlineGeometry = new THREE.BufferGeometry()

      barOutlineGeometry.addAttribute(
        'position',
        new THREE.Float32BufferAttribute(barOutlineVertices, 3)
      )

      barsGeometry.computeBoundingSphere()
      const barOutline = new THREE.Mesh(barOutlineGeometry, barOutlineMaterial)
      container.add(barOutline)
    }

    const tickPositions: number[] = []
    const tickZ = -10

    this.xTicks.forEach(xTick => {
      tickPositions.push(this.xScale(xTick), this.yScale.range()[0], tickZ)
      tickPositions.push(this.xScale(xTick), this.yScale.range()[1], tickZ)
    })

    const ySpan = Math.abs(this.yScale.range()[1] - this.yScale.range()[0])
    this.yTicks = yTicks || this.yScale.ticks(Math.round(ySpan / 50))
    this.yTicks.forEach((y: any) => {
      tickPositions.push(this.xScale.range()[0], this.yScale(y), tickZ)
      tickPositions.push(this.xScale.range()[1], this.yScale(y), tickZ)
    })

    const tickGeometry = new THREE.BufferGeometry()
    tickGeometry.addAttribute(
      'position',
      new THREE.Float32BufferAttribute(tickPositions, 3)
    )

    const ticks = new THREE.LineSegments(tickGeometry, material)
    container.add(ticks)

    const pointsGeometry = new THREE.BufferGeometry()
    const colors: number[] = []
    const outlines: number[] = []
    const positions: number[] = []
    const aboveDomainValues: number[] = []

    this.data.forEach(item => {
      colors.push(...item.color)
      outlines.push(item.outline)
      positions.push(this.xScale(item.x), this.yScale(item.y), 0)
      aboveDomainValues.push(item.aboveDomain ? 1 : 0)
    })

    pointsGeometry.addAttribute(
      'position',
      new THREE.Float32BufferAttribute(positions, 3)
    )

    pointsGeometry.addAttribute(
      'outline',
      new THREE.Float32BufferAttribute(outlines, 1)
    )

    pointsGeometry.addAttribute(
      'color',
      new THREE.Float32BufferAttribute(colors, 4)
    )

    pointsGeometry.addAttribute(
      'aboveDomain',
      new THREE.Float32BufferAttribute(aboveDomainValues, 1)
    )

    pointsMaterial.uniforms.scale.value = 20 * pixelRatio
    this.points = new THREE.Points(pointsGeometry, pointsMaterial)
    container.add(this.points)

    this.scene.updateMatrixWorld()
    this.labels = [
      {
        point: container.localToWorld(
          new THREE.Vector3(
            this.xScale.range()[0] + 3,
            this.yScale.range()[1] - 18,
            0
          )
        ),
        text: this.title,
        rotate: false,
        className: 'text-sm text-gray-600 font-normal',
      },
      ...this.xTicks.map(tick => {
        const labelPosition = container.localToWorld(
          new THREE.Vector3(this.xScale(tick), this.yScale.range()[0], 0)
        )

        return {
          point: labelPosition,
          text: tick,
          rotate: true,
          className: 'text-xs anchor-end text-gray-700',
        }
      }),
      ...this.yTicks.map((tick: number) => {
        const labelPosition = container.localToWorld(
          new THREE.Vector3(this.xScale.range()[0], this.yScale(tick), 0)
        )

        return {
          point: labelPosition,
          text: formatLap(tick),
          rotate: false,
          className: 'text-xs anchor-end text-gray-700',
        }
      }),
    ]

    if (this.message) {
      const messageLabel = {
        point: container.localToWorld(
          new THREE.Vector3(
            (this.xScale.range()[0] + this.xScale.range()[1]) / 2,
            (this.yScale.range()[0] + this.yScale.range()[1]) / 2,
            0
          )
        ),
        text: this.message,
        rotate: false,
      }

      this.labels.push({
        ...messageLabel,
        className: 'anchor-middle text-xs outline-white',
      })

      this.labels.push({
        ...messageLabel,
        className: 'anchor-middle text-xs',
      })
    }
  }
}
